// utils/locale-url.ts
import {i18n, type Locale} from "@/i18n-config";

/** Extract active [lang] from the first segment, e.g., /en/... → "en" */
export function getLangFromPath(pathname: string | null | undefined): Locale {
  const seg = (pathname || "/").split("/")[1];
  return (i18n.locales as readonly string[]).includes(seg || "") ? (seg as Locale) : i18n.defaultLocale;
}

/** Useful on the server where you have params from app/[lang]/... */
export function getLangFromParams(params: Record<string, string | string[] | undefined>): Locale {
  const seg = (params?.lang ?? params?.locale ?? params?.lng) as string | undefined;
  return (i18n.locales as readonly string[]).includes(seg || "") ? (seg as Locale) : i18n.defaultLocale;
}

/** Ignore external/file-like schemes and hash-only links */
const SPECIAL_SCHEMES = /^(https?:|mailto:|tel:|blob:|data:|file:|sandbox:)/i;

/** Core prefixer (pure, SSR-safe) */
export function withLang(href: string, lang: Locale): string {
  if (!href || href.startsWith("#") || SPECIAL_SCHEMES.test(href)) return href;

  // keep ?query/#hash intact
  const m = href.match(/^[^?#]+/);
  const path = m ? m[0] : href;
  const rest = href.slice(path.length);

  const normalized = path.startsWith("/") ? path : `/${path}`;
  const already = i18n.locales.some((l) => normalized === `/${l}` || normalized.startsWith(`/${l}/`));

  return `${already ? normalized : `/${lang}${normalized}`}${rest}`;
}

/** When you only have a pathname string (works on server/client) */
export function withLangFromPath(href: string, pathname: string | null | undefined): string {
  return withLang(href, getLangFromPath(pathname));
}

export function withAutoLang(href: string) {
  const pathname = typeof window !== "undefined" ? window.location.pathname : "/";
  return withLangFromPath(href, pathname);
}
